import React from 'react';
import { Typography, Box, Card, CardContent, List, ListItem, ListItemText, ListItemIcon, Chip } from '@mui/material';
import { Lightbulb, PlaylistAddCheck, QuestionAnswer, FactCheck, Flag, Assignment } from '@mui/icons-material';

const SECTION_CONFIG = [
  { key: 'summary', label: 'Summary', icon: <Lightbulb /> },
  { key: 'decisions', label: 'Decisions', icon: <FactCheck />, list: true },
  { key: 'nextSteps', label: 'Next Steps', icon: <PlaylistAddCheck />, list: true },
  { key: 'openQuestions', label: 'Open Questions', icon: <QuestionAnswer />, list: true },
  { key: 'actionItems', label: 'Action Items', icon: <Assignment />, list: true }
];

/**
 * Renders the structured semantic meeting analysis. Takes a normalized
 * `analysis` object from the domain module.
 */
const MeetingAnalysis = ({ analysis }) => {
  if (!analysis) return null;

  const hasContent =
    analysis.summary ||
    analysis.decisions.length > 0 ||
    analysis.nextSteps.length > 0 ||
    analysis.openQuestions.length > 0 ||
    analysis.actionItems.length > 0;

  if (!hasContent) return null;

  return (
    <Card data-testid="meeting-analysis">
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography variant="h6">Meeting Analysis</Typography>
          {analysis.title && (
            <Chip size="small" label={analysis.title} variant="outlined" />
          )}
        </Box>

        {SECTION_CONFIG.map(({ key, label, list }) => {
          const value = analysis[key];
          const items = list ? value : null;
          const plain = !list && value;
          if ((list && items.length === 0) || (!list && !plain)) return null;

          return (
            <Box key={key} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                {label}
              </Typography>
              {list ? (
                <List dense disablePadding>
                  {items.map((item, index) => {
                    const isObjectItem = item && typeof item === 'object';
                    const primary = isObjectItem ? item.task : item;
                    const secondary = isObjectItem && item.assignee ? `Assignee: ${item.assignee}` : undefined;
                    return (
                      <ListItem key={`${key}-${index}`} divider={index < items.length - 1} disableGutters>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Flag fontSize="small" color="action" />
                        </ListItemIcon>
                        <ListItemText primary={primary} secondary={secondary} />
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                  {plain}
                </Typography>
              )}
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default MeetingAnalysis;